import { useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { collection, query, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import type { Deal } from '@elka/types';
import type { DealQuotingStatus } from '@elka/types';

export interface DealWithQuotingStatus extends Deal {
  quotingStatus: DealQuotingStatus;
  costEstimateId?: string;
  quoteId?: string;
  quoteTotal?: number;
}

export function useDealsWithQuotingStatus() {
  const dealsQuery = useMemo(
    () => query(collection(db, ...splitPath(COLLECTIONS.crm.deals)), orderBy('createdAt', 'desc')),
    []
  );
  const [dealsSnap, dealsLoading, dealsError] = useCollection(dealsQuery);

  const deals = useMemo(() => {
    if (!dealsSnap) return [];

    return dealsSnap.docs.map((doc) => {
      const deal = { id: doc.id, ...doc.data() } as Deal;

      // quotingStatus and related fields are now stored on the deal document
      // Migrate old status values and default to 'draft' if not set
      const migrateStatus = (s: string | undefined): DealQuotingStatus => {
        if (!s) return 'draft';
        const migrationMap: Record<string, DealQuotingStatus> = {
          'no-estimate': 'draft',
          costing: 'estimating',
          accepted: 'quoted',
        };
        return migrationMap[s] || (s as DealQuotingStatus);
      };
      return {
        ...deal,
        quotingStatus: migrateStatus(deal.quotingStatus),
        costEstimateId: deal.costEstimateId,
        quoteId: deal.quoteId,
        quoteTotal: deal.quoteTotal,
      } as DealWithQuotingStatus;
    });
  }, [dealsSnap]);

  return {
    deals,
    dealsLoading,
    error: dealsError,
  };
}
